"use client"

import { memo, useCallback, useEffect, useRef } from "react"
import { useParams } from "next/navigation"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"

import { useAuthStore } from "@/lib/store/auth-store"
import { Category } from "@/lib/types"
import { useCartItems } from "@/features/cart/hooks/use-cart-detail"
import { addRestaurantItems } from "@/app/actions/order"

import { MenuItem } from "./menu-item"

interface MenuCategorySectionsProps {
  categories: Category[]
  activeCategory: string | null
  isManualScrolling: boolean
  onSectionInView: (categoryId: string) => void
  onSelectItem?: (itemId: number) => void
  isFiltered: boolean
  isClosed?: boolean
}

export const MenuCategorySections = memo(
  ({
    categories,
    // activeCategory,
    isManualScrolling,
    onSectionInView,
    onSelectItem,
    isFiltered,
    isClosed,
  }: MenuCategorySectionsProps) => {
    // References to track sections for intersection observation
    const sectionRefs = useRef<{ [key: string]: HTMLDivElement | null }>({})
    const params = useParams<{ id: string }>()
    const restaurantId = params.id
    const queryClient = useQueryClient()
    const userId = useAuthStore((state) => state.user?.user_id)

    // Get cart items for this restaurant
    const { getItemQuantity, restaurantCart } = useCartItems(restaurantId)

    // Centralized mutation for updating item quantities
    const {
      mutate: updateItemQuantity,
      isPending: isUpdating,
      variables,
    } = useMutation({
      mutationFn: async ({
        itemId,
        newQuantity,
        itemName,
        price,
        currentQuantity,
        cartItems,
        cartId,
      }: {
        itemId: number
        newQuantity: number
        itemName: string
        price: number
        currentQuantity: number
        cartItems?: any[]
        cartId?: string
        hasCustomizations?: boolean
      }) => {
        if (!userId || !restaurantId) {
          throw new Error("Missing user or restaurant information")
        }

        console.log({
          currentQuantity,
          newQuantity,
        })

        // Create cart item
        const cartItem = {
          item_id: itemId.toString(),
          quantity: newQuantity,
          price: price,
          customizes: [],
          note: "",
          item_name: itemName,
        }

        // If we have cartItems, use them (preserving customizations)
        // Otherwise just update this single item
        const items = cartItems || [cartItem]

        return addRestaurantItems(userId.toString(), restaurantId, items, {
          cartId,
        })
      },
      onSuccess: (_, variables) => {
        const { newQuantity, itemName, currentQuantity, hasCustomizations } =
          variables as any

        // Invalidate cart query to refresh data
        queryClient.invalidateQueries({ queryKey: ["cart"] })

        // Show success message
        if (newQuantity === 0) {
          toast.success("Đã xóa món ăn khỏi giỏ hàng", {
            description: `Đã xóa ${itemName} khỏi giỏ hàng`,
          })
        } else if (currentQuantity === 0 && !hasCustomizations) {
          toast.success("Đã thêm món ăn vào giỏ hàng", {
            description: `Đã thêm ${itemName} vào giỏ hàng`,
          })
        } else if (!(currentQuantity === 0 && hasCustomizations)) {
          toast.success("Đã cập nhật số lượng", {
            description: `Số lượng ${itemName}: ${newQuantity}`,
          })
        }
      },
      onError: (error) => {
        toast.error("Không thể cập nhật giỏ hàng", {
          description:
            error instanceof Error
              ? error.message
              : "Đã xảy ra lỗi khi cập nhật giỏ hàng",
        })
      },
    })

    // Handler for updating item quantity - memoized to prevent recreations
    const handleUpdateItemQuantity = useCallback(
      (
        itemId: number,
        newQuantity: number,
        itemName: string,
        price: number,
        currentQuantity: number,
        hasCustomizations: boolean
      ) => {
        // If the item has customizations and is being added for the first time, open dialog
        if (hasCustomizations && currentQuantity === 0 && newQuantity > 0) {
          onSelectItem?.(itemId)
          return
        }

        // Look for the item in the cart to preserve customizations when incrementing
        if (
          hasCustomizations &&
          currentQuantity > 0 &&
          newQuantity > currentQuantity
        ) {
          // Find all instances of this item in the cart
          const existingItems =
            restaurantCart?.restaurant_items.filter(
              (item) => item.item_id === itemId
            ) || []

          if (existingItems.length > 0) {
            // If we have multiple versions of the same item (with different customizations)
            // We should show the dialog to let the user choose which one to modify
            if (existingItems.length > 1) {
              onSelectItem?.(itemId)
              return
            }

            // Prepare all items to be sent to the API
            const allCartItems = (restaurantCart?.restaurant_items || []).map(
              (item) => {
                if (item.item_id === itemId) {
                  // Update the quantity of the matching item
                  return {
                    ...item,
                    quantity: newQuantity,
                  }
                }
                return item
              }
            )

            // Call the updateItemMutation with the cart ID to preserve all items
            updateItemQuantity({
              itemId,
              newQuantity,
              itemName,
              price,
              currentQuantity,
              cartItems: allCartItems,
              cartId: restaurantCart?.cart_id?.toString(),
              hasCustomizations,
            })
            return
          }
        }

        // Otherwise update quantity directly
        if (
          restaurantCart?.restaurant_items &&
          restaurantCart.restaurant_items.length > 0
        ) {
          // Prepare all items to be sent to the API to preserve all items in the cart
          const allCartItems = [...restaurantCart.restaurant_items]

          // Check if the item already exists in the cart
          const existingItemIndex = allCartItems.findIndex(
            (item) => item.item_id === itemId
          )

          if (existingItemIndex >= 0) {
            // Update existing item quantity
            allCartItems[existingItemIndex] = {
              ...allCartItems[existingItemIndex],
              quantity: newQuantity,
            }
          } else {
            // Add new item to cart items
            allCartItems.push({
              item_id: itemId,
              quantity: newQuantity,
              price: price,
              customizes: [],
              note: "",
              item_name: itemName,
              // Add required fields for RestaurantItem type
              modify_status: 0,
              order_item_id: 0,
              old_price: 0,
              customise_object: "",
              customizes_name: "",
              old_price_active: false,
              alcoholic_status: 0,
            })
          }

          updateItemQuantity({
            itemId,
            newQuantity,
            itemName,
            price,
            currentQuantity,
            cartItems: allCartItems,
            cartId: restaurantCart?.cart_id?.toString(),
            hasCustomizations,
          })
        } else {
          // No existing cart, just add the new item
          updateItemQuantity({
            itemId,
            newQuantity,
            itemName,
            price,
            currentQuantity,
            hasCustomizations,
          })
        }
      },
      [onSelectItem, updateItemQuantity, restaurantCart]
    )

    // Setup intersection observer to detect which section is in view
    useEffect(() => {
      const observerOptions = {
        root: null, // viewport
        rootMargin: "-100px 0px -70% 0px", // Adjust based on when you want to trigger the active state
        threshold: 0,
      }

      let debounceTimer: NodeJS.Timeout | null = null

      const observerCallback: IntersectionObserverCallback = (entries) => {
        // Skip if manual scrolling is in progress
        if (isManualScrolling) return

        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const categoryId = entry.target.id.replace("category-", "")

            // Clear any existing debounce timer
            if (debounceTimer) clearTimeout(debounceTimer)

            // Set a new debounce timer
            debounceTimer = setTimeout(() => {
              onSectionInView(categoryId)
            }, 100) // Small debounce to prevent rapid changes
          }
        })
      }

      const observer = new IntersectionObserver(
        observerCallback,
        observerOptions
      )

      // Observe all category sections
      Object.values(sectionRefs.current).forEach((section) => {
        if (section) observer.observe(section)
      })

      return () => {
        if (debounceTimer) clearTimeout(debounceTimer)
        observer.disconnect()
      }
    }, [categories, isManualScrolling, onSectionInView])

    if (categories.length === 0) {
      return (
        <div className="text-center py-8">
          <p className="text-gray-500">Không tìm thấy món ăn phù hợp</p>
        </div>
      )
    }

    console.log({ restaurantCart })

    return (
      <div className="flex-1">
        {categories.map((category: any) => {
          // Determine if the category is active (assuming `categoryActive` property)
          // Default to true if property doesn't exist for now
          const isCategoryActive =
            typeof category.categoryActive === "boolean"
              ? category.categoryActive
              : true

          return (
            <div
              key={`section-${category.category_id + (category.is_flash_sale ? "-flash-sale" : "")}`}
              id={`category-${category.category_id}`}
              ref={(el) => {
                sectionRefs.current[String(category.category_id)] = el
              }}
              className="mt-6 pt-4"
              role="tabpanel"
              aria-labelledby={`tab-${category.category_id}`}
              itemProp="hasMenuSection"
              itemScope
              itemType="https://schema.org/MenuSection"
            >
              <h3 className="text-2xl font-semibold mb-6" itemProp="name">
                {category.category_name}
              </h3>

              <div className="grid lg:grid-cols-2 gap-5">
                {(isFiltered ? category.filteredItems : category.items).map(
                  (item: any) => {
                    const currentQuantity = getItemQuantity(
                      item.restaurant_item_id
                    )
                    // Extract flash sale properties for the item, defaulting if not present
                    const itemIsFlashSale = !!category.isFlashSale // Item is flash sale if its category is
                    const itemMaxAllowPerDay = item.max_allow_per_day
                    const itemUsedQuantityInDay = item.used_quantity
                    // Determine if the item itself is active (assuming `isActive` property on item)
                    // Default to true if property doesn't exist for now
                    const isItemActive =
                      typeof item.isActive === "boolean" ? item.isActive : true

                    return (
                      <MenuItem
                        key={item.restaurant_item_id}
                        item={item}
                        isRestoClosed={isClosed}
                        onSelect={() => onSelectItem?.(item.restaurant_item_id)}
                        currentItemQuantity={currentQuantity}
                        onUpdateQuantity={handleUpdateItemQuantity}
                        isUpdating={
                          isUpdating &&
                          variables?.itemId === item.restaurant_item_id
                        }
                        // Pass new flash sale related props
                        isFlashSaleCategory={itemIsFlashSale}
                        categoryActive={isCategoryActive}
                        itemIsActive={isItemActive} // Pass item's own active status
                        maxAllowPerDay={itemMaxAllowPerDay}
                        usedQuantityInDay={itemUsedQuantityInDay}
                      />
                    )
                  }
                )}
              </div>
            </div>
          )
        })}
      </div>
    )
  }
)

MenuCategorySections.displayName = "MenuCategorySections"
